import { type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { Button } from 'Button';
import { Text } from 'Text';
import '../locales/streamDashboard/streamDashboard-en.json';

type Props = {
    mustVerifyIdentity: boolean;
    isExam: boolean;
    hasLessonProgress: boolean;
    hasExamVerificationRecord: boolean;
    canLaunch: boolean;
    onStart: () => void;
    className?: string;
};

const getCalloutKey = ({
    mustVerifyIdentity,
    isExam,
    hasLessonProgress,
    hasExamVerificationRecord,
}: Pick<Props, 'mustVerifyIdentity' | 'isExam' | 'hasLessonProgress' | 'hasExamVerificationRecord'>) => {
    if (mustVerifyIdentity) {
        return hasLessonProgress ? 'ready_to_continue_verify_identity' : 'ready_to_begin_verify_identity';
    }
    if (isExam) {
        return hasExamVerificationRecord ? 'verified_identity_thanks' : 'ready_to_begin_exam';
    }
    return 'ready_to_begin_start_learning';
};

const getButtonKey = ({ mustVerifyIdentity, isExam }: Pick<Props, 'mustVerifyIdentity' | 'isExam'>) => {
    if (mustVerifyIdentity) return 'verify_identity';
    return isExam ? 'start' : 'start_course';
};

export const StartCourseBox: FC<Props> = ({
    mustVerifyIdentity,
    isExam,
    hasLessonProgress,
    hasExamVerificationRecord,
    canLaunch,
    onStart,
    className,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    return (
        <div
            className={twMerge(
                /*
                    The same chrome as a sidebar box, which is what the legacy grid-box carried here:
                    16px radius, a slate-grey-200 border, and a 0 2px 10px shadow at 10% black. The shadow
                    is the arbitrary value Card uses for its own default, matched rather than reinvented.
                */
                'flex flex-col items-center gap-3.5 rounded-8 border border-slate-grey-200 bg-white p-5',
                'shadow-[0_2px_10px_0_rgba(0,0,0,0.1)] sm:flex-row sm:justify-between',
                className,
            )}
        >
            {/* Trans, not t: one callout has a line-break slot below sm. */}
            {/* Centred below sm, which is what `.callout { text-align: center }` did inside the legacy
                box's max-width: $screen-xs-max block. It matters now that the callout breaks onto two
                lines there. From sm up the box is a row and the text sits at the start.

                20px below sm and 24px from sm up. The legacy rules were 20px and 23px; 20px is an
                exact scale step, and 23px is not, so only the desktop size rounds. */}
            <Text as="div" className="text-center text-lg text-black sm:text-start sm:text-xl">
                <Trans
                    t={t}
                    i18nKey={getCalloutKey({
                        mustVerifyIdentity,
                        isExam,
                        hasLessonProgress,
                        hasExamVerificationRecord,
                    })}
                    components={{ lineBreak: <br className="sm:hidden" /> }}
                />
            </Text>
            {/* 218px in the legacy rule; w-56 is 224px, the nearest step. Purple once the course has
                progress, which is what `.start-course-box button.inProgress` set. */}
            <Button
                color={hasLessonProgress ? 'purple' : 'green'}
                onPress={onStart}
                className="w-full shrink-0 sm:w-56"
            >
                {t(getButtonKey({ mustVerifyIdentity, isExam }))}
                {!canLaunch && <FontAwesomeIcon icon={faLock} className="ms-2" />}
            </Button>
        </div>
    );
};
